import React, { useState, useEffect, useContext, useMemo } from 'react'
import { strCol } from '@integec/grid-tools/lib/cols'
import { modifierCol, modifiedDateCol } from 'core/HeaderColTypeProps'
import { connect } from 'react-redux'
import {
  getApiTargetInfo,
  hasAccesPermits,
  getPermitActions,
  getEnvConfig,
} from 'core'
import { Input, Button, Select } from 'antd'
import CommonScreen from 'admin/component/CommonScreen'
import {
  getTurbinePointsInterface,
  postTurbinePointsInterface,
  deleteTurbinePointsInterface,
  getWindSubgroup,
  getWindPlantInfo,
} from 'api/pgads'
import { toSelectionColProps } from '@integec/grid-tools/lib/utils'
import { ErrorContext } from 'context'
import R from 'ramda'
import * as ScreenPermissions from 'core/EventActions'

const typeMap = { SNAP: 'SNAP', RAW: 'RAW' }

// const retrieveTask = apiTargetInfo => getTurbinePointsInterface(apiTargetInfo)

const modifyTask = apiTargetInfo => data =>
  postTurbinePointsInterface(apiTargetInfo)(data)

const deleteTask = apiTargetInfo => data =>
  deleteTurbinePointsInterface(apiTargetInfo)(data)

const title = 'Turbine Points'

const TurbinPoints = ({
  apiTargetInfo,
  envConfig,
  actionPermits,
  // eslint-disable-next-line no-unused-vars
  ...props
}) => {
  const [insertEvent, setInsertEvent] = useState(false)

  const { setError } = useContext(ErrorContext)

  const [subGroup, setSubgroup] = useState({})
  const [plant, setPlant] = useState({})
  const [plantSubgroup, setPlantSubgroup] = useState({})

  const writePermision = hasAccesPermits({
    authGodMode: envConfig.authGodMode,
    actionPermits,
    eventActions: [ScreenPermissions.REPORT_PERFORMANCE_MEASURES_ADMIN],
    matchAny: true,
  })

  useEffect(
    _ => {
      getWindSubgroup(apiTargetInfo).fork(setError, d => {
        const subgroupMap = R.reduce(
          (a, b) => ({ ...a, ...b }),
          {},
          d.map(s => ({ ['' + s.subgroupId]: s.subgroupName }))
        )
        setSubgroup(subgroupMap)

        const groupByArray = R.compose(
          R.reduce((a, b) => ({ ...a, ...b }), []),
          R.map(([k, v]) => ({ [k]: (v || []).map(z => z.subgroupId) })),
          R.toPairs,
          R.groupBy(d => d.plantIdName)
        )(d)

        setPlantSubgroup(groupByArray)
      })
      getWindPlantInfo(apiTargetInfo).fork(setError, d => {
        const plantMap = R.reduce(
          (a, b) => ({ ...a, ...b }),
          {},
          d.map(s => ({ [s.plantIdName]: s.plantIdName }))
        )
        setPlant(plantMap)
      })
    },
    [apiTargetInfo, setError]
  )

  const insertComp = ({ insertData, setInsertData, onAdd, onCancel }) => {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            '150px 150px 100px 200px  100px 150px 100px 150px',
          gridGap: '10px',
        }}
      >
        <div
          style={{
            display: 'grid',
            alignContent: 'center',
            justifyContent: 'end',
          }}
        >
          Plant :{' '}
        </div>

        <Select
          value={insertData.plant}
          onChange={plant =>
            setInsertData({ ...insertData, plant, turbine: undefined })
          }
          style={{ gridColumn: 'span 3' }}
        >
          {Object.keys(plant).map(k => (
            <Select.Option value={k} key={k}>
              {k}
            </Select.Option>
          ))}
        </Select>

        <div
          style={{
            display: 'grid',
            alignContent: 'center',
            justifyContent: 'end',
          }}
        >
          Subgroup :{' '}
        </div>

        <Select
          value={insertData.turbine}
          onChange={turbine => setInsertData({ ...insertData, turbine })}
          style={{ gridColumn: 'span 3' }}
        >
          {(plantSubgroup[insertData.plant] || []).map(k => (
            <Select.Option value={k} key={k}>
              {subGroup[k + '']}
            </Select.Option>
          ))}
        </Select>

        <div
          style={{
            display: 'grid',
            alignContent: 'center',
            justifyContent: 'end',
          }}
        >
          Turbine Point ID :{' '}
        </div>

        <Input
          value={insertData.pointid}
          onChange={e =>
            setInsertData({ ...insertData, pointid: e.target.value })
          }
        />

        <div
          style={{
            display: 'grid',
            alignContent: 'center',
            justifyContent: 'end',
          }}
        >
          Type :{' '}
        </div>

        <Select
          value={insertData.type}
          onChange={type => setInsertData({ ...insertData, type })}
        >
          {Object.keys(typeMap).map(t => (
            <Select.Option key={t} value={t}>
              {t}
            </Select.Option>
          ))}
        </Select>
        <div />
        <Button type="primary" onClick={() => onAdd(true)}>
          Add
        </Button>

        <Button type="primary" onClick={() => onAdd(false)}>
          Add & Hide
        </Button>
        <Button type="danger" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    )
  }

  const EdnaPointsHeaders = useMemo(
    _ => [
      strCol({
        ident: 'plant',
        display: 'Plant',
        isFiltered: true,
        sortOrder: 'asc',
        isKey: true,
        width: 200,
      }),
      strCol({
        ident: 'turbine',
        display: 'Sub-Group',
        isKey: true,
        sortOrder: 'asc',
        width: 200,
        ...toSelectionColProps(subGroup),
        sortIndexGetter: ({ rowData }) =>
          subGroup[rowData.turbine] || rowData.turbine,
      }),
      strCol({
        ident: 'pointid',
        display: 'Turbine Point ID',
        isKey: true,
        sortOrder: 'asc',
        width: 200,
      }),
      strCol({
        ident: 'type',
        display: 'Type',
        isFiltered: true,
        sortOrder: 'asc',
        ...toSelectionColProps(typeMap),
      }),
      modifierCol,
      modifiedDateCol,
    ],
    [subGroup]
  )

  return (
    <CommonScreen
      isAdminScreen={false}
      title={title}
      header={EdnaPointsHeaders}
      saveTask={modifyTask}
      retrieveTask={getTurbinePointsInterface}
      deleteTask={deleteTask}
      altBy={d => d.turbine}
      autoRetrieve
      sortOptions={EdnaPointsHeaders}
      insertCompHeight={150}
      onInsertEvent={b => setInsertEvent(b)}
      withoutModify={!writePermision}
      allowInsert={writePermision}
      insertComp={writePermision && insertEvent && insertComp}
      // widthOffset={150}
    />
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  actionPermits: getPermitActions(state),
  envConfig: getEnvConfig(state),
})

export default connect(mapStateToProps)(TurbinPoints)
